import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import TopNav from '../../components/layout/TopNav';
import api from '../../services/api';
import type { Tenant, LeaderboardEntry } from '../../types';

function scoreGrade(score: number): string {
  if (score >= 85) return 'A';
  if (score >= 70) return 'B';
  if (score >= 55) return 'C';
  if (score >= 40) return 'D';
  return 'F';
}

const GRADE_COLORS: Record<string, string> = {
  A: 'text-emerald-500',
  B: 'text-primary',
  C: 'text-amber-500',
  D: 'text-orange-500',
  F: 'text-red-500',
};

const GroupAdminTenantsPage: React.FC = () => {
  const navigate = useNavigate();
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [metrics, setMetrics] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const [tenantsRes, metricsRes] = await Promise.all([
          api.get('/group-admin/tenants'),
          api.get('/group-admin/tenants/metrics'),
        ]);
        setTenants(tenantsRes.data.data ?? []);
        setMetrics(metricsRes.data.data ?? []);
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, []);

  const metricsMap = Object.fromEntries(metrics.map((m) => [m.tenantId, m]));

  const filtered = tenants.filter((t) =>
    !search || t.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-slate-50/50 dark:bg-slate-950/50 text-slate-900 dark:text-white transition-colors duration-300 font-sans flex flex-col">
      <TopNav />
      <main className="flex-1 w-full max-w-7xl mx-auto px-6 py-12">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 dark:text-white tracking-tight leading-tight">
              Dealerships
            </h1>
            <p className="text-slate-500 dark:text-slate-400 font-medium text-base mt-2 flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-primary" />
              {tenants.length} dealership{tenants.length !== 1 ? 's' : ''} in your group
            </p>
          </div>

          <div className="relative">
            <span className="material-symbols-outlined absolute left-4 top-1/2 -translate-y-1/2 text-[18px] text-slate-400">search</span>
            <input
              type="text"
              placeholder="Search dealerships..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-10 pl-10 pr-5 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-sm font-medium text-slate-900 dark:text-white placeholder:text-slate-400 outline-none focus:border-primary focus:ring-4 focus:ring-primary/5 transition-all w-64"
            />
          </div>
        </div>

        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20 gap-4">
              <div className="w-8 h-8 border-3 border-primary border-t-transparent animate-spin rounded-full shadow-[0_0_10px_rgba(var(--primary-rgb),0.2)]" />
              <span className="text-sm font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest">Loading dealerships...</span>
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 gap-4">
              <span className="material-symbols-outlined text-[48px] text-slate-300 dark:text-slate-700">business_off</span>
              <p className="text-slate-500 dark:text-slate-400 font-medium text-base">
                {search ? 'No dealerships match your search.' : 'No dealerships assigned to this group.'}
              </p>
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50/50 dark:bg-slate-800/50 border-b border-slate-100 dark:border-slate-800">
                  <th className="text-left px-8 py-5 text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-500">Dealership</th>
                  <th className="text-left px-6 py-5 text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-500">Status</th>
                  <th className="text-right px-6 py-5 text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-500">Videos</th>
                  <th className="text-right px-6 py-5 text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-500">Avg Score</th>
                  <th className="text-right px-6 py-5 text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-500">Grade</th>
                  <th className="text-right px-6 py-5 text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-500">Flag Rate</th>
                  <th className="text-right px-8 py-5 text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-500"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {filtered.map((t) => {
                  const m = metricsMap[t.id];
                  const grade = m && m.totalVideos > 0 ? scoreGrade(m.averageScore) : null;
                  return (
                    <tr
                      key={t.id}
                      className="hover:bg-slate-50/50 dark:hover:bg-white/[0.02] transition-colors cursor-pointer"
                      onClick={() => navigate(`/group-admin/tenants/${t.id}`)}
                    >
                      <td className="px-8 py-5">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-xl bg-primary/10 dark:bg-primary/20 flex items-center justify-center text-primary font-bold text-sm shrink-0">
                            {t.name.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <span className="font-bold text-slate-900 dark:text-white text-sm block">{t.name}</span>
                            <span className="font-mono text-[10px] text-slate-400 dark:text-slate-500">{t.slug}</span>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-5">
                        <div className={`inline-flex items-center gap-2 text-xs font-bold uppercase tracking-tight ${t.isActive ? 'text-primary' : 'text-slate-400'}`}>
                          <span className={`w-2 h-2 rounded-full ${t.isActive ? 'bg-primary' : 'bg-slate-300 dark:bg-slate-600'}`} />
                          {t.isActive ? 'Active' : 'Inactive'}
                        </div>
                      </td>
                      <td className="px-6 py-5 text-right">
                        {m ? (
                          <span className="text-sm font-bold text-slate-900 dark:text-white">{m.totalVideos.toLocaleString()}</span>
                        ) : (
                          <span className="text-xs text-slate-300 dark:text-slate-600">—</span>
                        )}
                      </td>
                      <td className="px-6 py-5 text-right">
                        {m && m.totalVideos > 0 ? (
                          <span className="text-sm font-black text-slate-900 dark:text-white">{m.averageScore.toFixed(1)}</span>
                        ) : (
                          <span className="text-xs text-slate-300 dark:text-slate-600">—</span>
                        )}
                      </td>
                      <td className="px-6 py-5 text-right">
                        {grade ? (
                          <span className={`text-sm font-black ${GRADE_COLORS[grade] ?? 'text-slate-400'}`}>{grade}</span>
                        ) : (
                          <span className="text-xs text-slate-300 dark:text-slate-600">—</span>
                        )}
                      </td>
                      <td className="px-6 py-5 text-right">
                        {m && m.totalVideos > 0 ? (
                          <span className={`text-sm font-bold ${m.complianceFlagRate > 0.25 ? 'text-red-500' : m.complianceFlagRate > 0.1 ? 'text-amber-500' : 'text-emerald-500'}`}>
                            {(m.complianceFlagRate * 100).toFixed(1)}%
                          </span>
                        ) : (
                          <span className="text-xs text-slate-300 dark:text-slate-600">—</span>
                        )}
                      </td>
                      <td className="px-8 py-5 text-right">
                        <span className="material-symbols-outlined text-[16px] text-slate-300 dark:text-slate-600 group-hover:text-primary">chevron_right</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </main>
    </div>
  );
};

export default GroupAdminTenantsPage;
